<script>
import { mapGetters } from 'vuex';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import customDashboardsMixin from 'shared/mixins/customDashboardsMixin';
import Spinner from 'shared/components/Spinner.vue';
import { useAlert } from 'dashboard/composables';
import { emitter } from 'shared/helpers/mitt';

/* eslint-disable vue/no-unused-components */
import DashboardLayout from '../components/customDashboards/DashboardLayout.vue';
import EditWidgetModal from '../components/customDashboards/EditWidgetModal.vue';

export default {
  name: 'CustomDashboards',
  components: {
    DashboardLayout,
    Spinner,
    EditWidgetModal,
  },
  mixins: [customDashboardsMixin],
  validations: {
    dashboardTitle: {
      required,
    },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      widgets: [],
      dashboardTitle: '',
      dashboardDescription: '',
      selectedWidgetTypeOption: '',
      showDeleteModal: false,
      selectedWidgetIdToDelete: '',

      showWidgetEditModal: false,

      attachedFiles: [],
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'customDashboards/getUIFlags',
    }),
    dashboardLayoutComponent() {
      return document.getElementById('dashboard_layout').ej2_instances[0];
    },
    dashboardId() {
      return Number(this.$route.params.dashboardId);
    },
    currentDashboard() {
      return (
        this.customDashboards?.find(
          dashboard => dashboard.id === this.dashboardId
        ) || {}
      );
    },
    widgetTypeSettings() {
      return this.widgetOptionsList.find(
        e => e.type === this.selectedWidgetTypeOption
      );
    },
    hasStaticImageWidgetOnDashboard() {
      return this.widgets.some(
        widget => widget.contentProps.display.type === 'static_image'
      );
    },
  },
  watch: {
    currentDashboard() {
      this.setInitialData();
    },
  },
  mounted() {
    const currentDashboardIsEmpty = !Object.keys(this.currentDashboard).length;
    if (!currentDashboardIsEmpty) this.setInitialData();
  },
  created() {
    emitter.on('openEditWidgetModal', this.openWidgetEditorModal);
  },
  beforeUnmount() {
    emitter.off('openEditWidgetModal', this.openWidgetEditorModal);
  },
  methods: {
    setInitialData() {
      this.dashboardTitle = this.currentDashboard.title;
      this.dashboardDescription = this.currentDashboard.description;
      this.widgets = this.currentDashboard.settings?.layout?.metrics || [];
    },

    constructDashboardSettingsPayload() {
      const widgetsListData = this.widgets.map(widget =>
        this.extractWidgetProperties(widget)
      );

      return {
        layout: {
          metrics: widgetsListData,
        },
      };
    },
    async saveDashboard() {
      const alert = i18nPath => useAlert(this.$t(i18nPath));

      if (!this.widgets.length) {
        alert('CUSTOM_DASHBOARDS.CREATE_DASHBOARD.AT_LEAST_ONE_WIDGET');
        return;
      }

      try {
        let payload = {
          id: this.dashboardId,
          title: this.dashboardTitle,
          description: this.dashboardDescription || '',
          settings: this.constructDashboardSettingsPayload(),
        };
        if (this.attachedFiles && this.attachedFiles.length > 0)
          payload.attachment = this.attachedFiles[0].resource.file;

        await this.$store.dispatch('customDashboards/update', {
          id: this.dashboardId,
          payload,
        });
        alert('CUSTOM_DASHBOARDS.EDIT_DASHBOARD.API.SUCCESS_MESSAGE');
        this.fetchCurrentCustomDashboards();
        this.redirectToDashboardPage(this.dashboardId);
      } catch (e) {
        alert('CUSTOM_DASHBOARDS.EDIT_DASHBOARD.API.ERROR_MESSAGE');
      }
    },

    createNewWidget() {
      if (!this.canAddMoreWidgets()) return;
      const widgetOptions = {
        type: this.widgetTypeSettings.type, // type of widget
        header: this.widgetTypeSettings.title, // default header
      };

      this.$refs.dashboard_layout.createNewWidget(widgetOptions);
    },

    updateWidgetsListWithChildValue(widgetsList) {
      this.widgets = widgetsList;
    },

    confirmDeletion() {
      const widgetId = this.selectedWidgetIdToDelete;
      this.$refs.dashboard_layout.deleteWidget(widgetId);
      this.showDeleteModal = false;
      this.selectedWidgetIdToDelete = '';
    },

    openWidgetEditorModal(widgetInfos) {
      this.showWidgetEditModal = true;
      this.widgetBeingEdited = this.widgets.find(
        widget => widget.id === widgetInfos.id
      );
    },
    closeWidgetEditModal() {
      this.showWidgetEditModal = false;
      this.widgetBeingEdited = {};
    },
    editCurrentWidget(contentProps) {
      const widgetId = this.widgetBeingEdited.id;
      this.$refs.dashboard_layout.updateWidget(widgetId, contentProps);
      // AO FINAL DA EDIÇÃO RESETO AS VARIÁVEIS
      this.closeWidgetEditModal();
    },
    updateFilesFromChild(files) {
      this.attachedFiles = files;
    },
  },
};
</script>

<template>
  <Spinner v-if="uiFlags.isFetching" />
  <div v-else class="p-4">
    <div>
      <div class="flex justify-between">
        <!-- START TITLE SECTION -->
        <div class="w-full">
          <p class="text-xs mb-2 font-medium">
            {{ $t('CUSTOM_DASHBOARDS.EDIT_DASHBOARD.TITLE') }}
          </p>
          <input
            v-model="dashboardTitle"
            type="text"
            class="max-w-[30rem]"
            :style="{ border: v$.dashboardTitle.$error ? '1px solid red' : '' }"
            :placeholder="
              $t('CUSTOM_DASHBOARDS.EDIT_DASHBOARD.TITLE_PLACEHOLDER')
            "
            @input="v$.dashboardTitle.$touch"
          />
        </div>
        <!-- END TITLE SECTION -->
        <!-- START SAVE DASHBOARD BUTTON SECTION -->
        <div class="flex items-center pr-2">
          <woot-button
            color-scheme="success"
            icon="save"
            :disabled="v$.$invalid"
            @click="saveDashboard"
          >
            {{ $t('CUSTOM_DASHBOARDS.EDIT_DASHBOARD.SAVE_BUTTON') }}
          </woot-button>
        </div>
        <!-- END SAVE DASHBOARD BUTTON SECTION -->
      </div>

      <!-- START DESCRIPTION SECTION -->
      <div class="w-full">
        <p class="text-xs mb-2 font-medium">
          {{ $t('CUSTOM_DASHBOARDS.EDIT_DASHBOARD.DESCRIPTION') }}
        </p>
        <textarea
          v-model.trim="dashboardDescription"
          type="text"
          rows="4"
          class="max-w-[30rem]"
          :placeholder="
            $t('CUSTOM_DASHBOARDS.EDIT_DASHBOARD.DESCRIPTION_PLACEHOLDER')
          "
        />
      </div>
      <!-- END DESCRIPTION SECTION -->

      <!-- START ADD WIDGET SECTION -->
      <div class="max-w-[30rem] mb-5">
        <p class="text-xs mb-2 font-medium">
          {{ $t('CUSTOM_DASHBOARDS.ADD_WIDGET.HEADER') }}
        </p>

        <div class="flex items-center gap-3">
          <div class="w-full">
            <select v-model="selectedWidgetTypeOption" class="m-0">
              <option value="" disabled>
                {{ $t('CUSTOM_DASHBOARDS.ADD_WIDGET.DESCRIPTION') }}
              </option>
              <option
                v-for="widget in widgetOptionsList"
                :key="widget.type"
                :value="widget.type"
              >
                {{ widget.title }}
              </option>
            </select>
          </div>
          <div class="flex items-center">
            <woot-button
              color-scheme="primary"
              icon="add"
              class="w-[7rem]"
              :disabled="!selectedWidgetTypeOption"
              @click="createNewWidget"
            >
              {{ $t('CUSTOM_DASHBOARDS.ADD_WIDGET.BUTTON_LABEL') }}
            </woot-button>
          </div>
        </div>
      </div>
      <!-- END ADD WIDGET SECTION -->

      <!-- START DELETE WIDGET SECTION -->
      <div class="max-w-[30rem] mb-5">
        <p class="text-xs mb-2 font-medium">
          {{ $t('CUSTOM_DASHBOARDS.DELETE_WIDGET.HEADER') }}
        </p>

        <div class="flex items-center gap-3">
          <div class="w-full">
            <select v-model="selectedWidgetIdToDelete" class="m-0">
              <option
                v-for="widget in widgets"
                :key="widget.id"
                :value="widget.id"
              >
                {{ widget.headerText }}
              </option>
            </select>
          </div>
          <div class="flex items-center">
            <woot-button
              color-scheme="alert"
              icon="delete"
              class="w-[7rem]"
              :disabled="!selectedWidgetIdToDelete"
              @click="showDeleteModal = true"
            >
              {{ $t('CUSTOM_DASHBOARDS.DELETE_WIDGET.DELETE') }}
            </woot-button>
          </div>
        </div>
      </div>
      <!-- END DELETE WIDGET SECTION -->
    </div>

    <!-- START DASHBOARD SECTION -->
    <div>
      <p class="text-xs mb-2 font-medium">
        {{ $t('CUSTOM_DASHBOARDS.EDIT_DASHBOARD.DASHBOARD_VISUALIZATION') }}
      </p>
      <div
        class="mx-0 bg-white dark:bg-slate-800 rounded-[4px] p-4 border border-solid border-slate-75 dark:border-slate-700"
      >
        <DashboardLayout
          ref="dashboard_layout"
          edit-mode
          :metrics-from-a-p-i="widgets"
          @update-widgets-list="updateWidgetsListWithChildValue"
        />
      </div>
    </div>
    <!-- END DASHBOARD SECTION -->

    <woot-delete-modal
      v-if="showDeleteModal"
      v-model:show="showDeleteModal"
      class="context-menu--delete-modal"
      :on-close="() => (showDeleteModal = false)"
      :on-confirm="confirmDeletion"
      :title="$t('CUSTOM_DASHBOARDS.DELETE_WIDGET.TITLE')"
      :message="$t('CUSTOM_DASHBOARDS.DELETE_WIDGET.MESSAGE')"
      :confirm-text="$t('CUSTOM_DASHBOARDS.DELETE_WIDGET.DELETE')"
      :reject-text="$t('CUSTOM_DASHBOARDS.DELETE_WIDGET.CANCEL')"
    />
    <EditWidgetModal
      v-if="showWidgetEditModal"
      :show="showWidgetEditModal"
      :widget-being-edited="widgetBeingEdited"
      :has-static-image-widget-on-dashboard="hasStaticImageWidgetOnDashboard"
      @edit-current-widget="editCurrentWidget"
      @close-modal="closeWidgetEditModal"
      @update-attached-files="updateFilesFromChild"
    />
  </div>
</template>
